import React, { forwardRef } from "react";
import { Typography, Row, Col } from "antd";
import { LoanRepayment } from "../../types/loanRepayment";
import { SupplierPayment } from "../../types/supplierPayment";
import { useTranslation } from "react-i18next";
import logo from "../../assets/servicelogo.jpg";
import logo_ from "../../assets/felicitysolarlogo.jpg";

// Replace with your real logo
const LOGO_URL = logo;
const logoURL = logo_;
const { Title, Text } = Typography;

interface PaymentInvoiceProps {
  type: "loan" | "supplier";
  invoice: LoanRepayment | SupplierPayment;
  getPartyName: () => string;
  getPaymentMethodLabel: (code?: string) => string;
}

const PaymentInvoice = forwardRef<HTMLDivElement, PaymentInvoiceProps>(
  ({ type, invoice, getPartyName, getPaymentMethodLabel }, ref) => {
    const { t, i18n } = useTranslation();
    const isLoan = type === "loan";
    const isRTL = i18n.dir() === "rtl";

    // Details to show (labels via t())
    const details: { label: string; value: string | number | undefined }[] = [
      {
        label: isLoan ? t("repayment_number") : t("payment_number"),
        value: isLoan
          ? (invoice as LoanRepayment).repaymentNumber
          : (invoice as SupplierPayment).paymentNumber,
      },
      { label: t("date"), value: new Date(invoice.date).toLocaleString() },
      { label: isLoan ? t("customer") : t("supplier"), value: getPartyName() },
      { label: t("phone"), value: isLoan ? (invoice as LoanRepayment).customerPhone : (invoice as SupplierPayment).supplierPhone },
      { label: t("payment_method"), value: getPaymentMethodLabel(invoice.paymentMethod) },
      { label: t("reference"), value: invoice.reference || "-" },
      { label: t("note"), value: invoice.note || "-" }
    ];

    return (
      <div
        ref={ref}
        style={{
          width: "100vw",
          minHeight: "297mm",
          maxWidth: "210mm",
          margin: 0,
          background: "linear-gradient(135deg, #F7F9FC 0%, #e3f4fd 100%)",
          padding: "0",
          fontFamily: "Inter, Arial, sans-serif",
          color: "#262643",
          boxSizing: "border-box",
          direction: isRTL ? "rtl" : "ltr",
          position: "relative",
        }}
      >
        <style>
          {`
            @media print {
              @page { size: A4; margin: 0; }
              body { background: none !important; }
              .invoice-watermark { display: block !important; }
            }
            .invoice-watermark {
              position: absolute;
              top: 48%;
              left: 50%;
              transform: translate(-50%, -50%) rotate(-15deg);
              opacity: 0.11;
              font-size: 8.6rem;
              font-weight: 900;
              color: #2e8c60;
              z-index: 0;
              letter-spacing: 7px;
              pointer-events: none;
              user-select: none;
              white-space: nowrap;
              display: none;
            }
            .invoice-header-table {
              width: 100%;
              border-collapse: separate;
              border-spacing: 0;
              background: #fff;
              border-radius: 0 0 22px 22px;
              box-shadow: 0 5px 32px #d4e3f5;
              border-bottom: 3px solid #2850A7;
              min-height: 120px;
              margin-bottom: 0;
              direction: ${isRTL ? "rtl" : "ltr"};
            }
            .invoice-logo-img {
              width: 90px;
              height: 90px;
              border-radius: 16px;
              object-fit: contain;
              background: #fff;
              border: 2.5px solid #e3f4fd;
              box-shadow: 0 2px 18px #b5d2f7;
              padding: 8px;
            }
            .invoice-header-content {
              min-width: 260px;
              text-align: ${isRTL ? "right" : "left"};
              padding-top: 12px;
            }
            .invoice-status {
              background: #eafbf4;
              color: #18c48f;
              border: 2px dashed #68e7be;
              font-weight: 900;
              font-size: 1.2rem;
              border-radius: 13px;
              padding: 7px 26px;
              box-shadow: 0 1px 8px #d3f8ef33;
              min-width: 120px;
              display: flex;
              align-items: center;
              gap: 7px;
              justify-content: center;
            }
            .invoice-main-content {
              background: #fff;
              margin: 38px 44px 0 44px;
              border-radius: 18px;
              box-shadow: 0 2px 30px #d7e9fb63;
              padding: 46px 52px 32px 52px;
              min-height: 520px;
              position: relative;
              z-index: 1;
            }
            .invoice-amount-block {
              display: flex;
              align-items: center;
              justify-content: center;
              margin-bottom: 18px;
            }
            .invoice-amount-value {
              font-size: 3.7rem;
              font-weight: 900;
              color: #20725d;
              background: #eafbf4;
              border-radius: 18px;
              border: 2.5px dashed #60d394;
              display: inline-block;
              padding: 10px 42px;
              box-shadow: 0 1px 11px #20a8830b;
              margin-bottom: 10px;
              letter-spacing: 2px;
            }
            .invoice-details-table {
              width: 100%;
              border-collapse: collapse;
              margin: 0 auto 36px auto;
              background: none;
              font-size: 1.13rem;
            }
            .invoice-details-table th {
              background: #eef4ff;
              color: #2850a7;
              font-weight: 700;
              padding: 10px 8px;
              border-bottom: 2px solid #e7efff;
              text-align: ${isRTL ? "right" : "left"};
            }
            .invoice-details-table td {
              padding: 9px 8px;
              border-bottom: 1px solid #e7efff;
              text-align: ${isRTL ? "right" : "left"};
              color: #222;
              font-weight: 600;
            }
            .invoice-sign-block {
              display: flex;
              justify-content: flex-end;
              margin-top: 40px;
            }
            .invoice-sign-label {
              font-size: 1.13rem;
              color: #444a;
              border-bottom: 1.5px solid #96aed5;
              min-width: 155px;
              padding-bottom: 7px;
              text-align: center;
              font-weight: 500;
              letter-spacing: 2px;
            }
          `}
        </style>

        {/* Watermark (for print) */}
        <div className="invoice-watermark">{t("paid")}</div>

        {/* HEADER as table for better alignment and print UX */}
        <table className="invoice-header-table">
          <tbody>
            <tr>
              {/* Left Logo & Info */}
              <td style={{ width: 160, verticalAlign: 'middle', textAlign: isRTL ? 'right' : 'left', padding: 0 }}>
                <img src={LOGO_URL} alt="logo" className="invoice-logo-img" />
              </td>
              {/* Center Content */}
              <td style={{ textAlign: 'center', verticalAlign: 'middle', padding: 0 }}>
                <div className="invoice-header-content">
                  <Title
                    level={2}
                    style={{
                      fontWeight: 900,
                      color: "#2850A7",
                      fontSize: "2.1rem",
                      margin: 0,
                      letterSpacing: 1,
                      marginBottom: 6,
                      textShadow: "0 2px 8px #e3f4fd"
                    }}
                  >
                    {isLoan ? t("loan_repayment_invoice") : t("supplier_payment_invoice")}
                  </Title>
                  <Text style={{ fontWeight: 600, fontSize: 18, color: "#568", display: "block", marginBottom: 4 }}>
                    07509927709 &nbsp;|&nbsp; 07509927708
                  </Text>
                  <Text style={{ fontWeight: 500, fontSize: 17, color: "#568", display: "block", marginBottom: 2 }}>
                    {t("company_address")}
                  </Text>
                </div>
              </td>
              {/* Right Logo & Status */}
              <td style={{ width: 160, verticalAlign: 'middle', textAlign: isRTL ? 'left' : 'right', padding: 0 }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: isRTL ? 'flex-start' : 'flex-end', gap: 10 }}>
                  <img src={logoURL} alt="logo" className="invoice-logo-img" />
                  <div className="invoice-status">
                    <span style={{ fontSize: 22 }}>✔</span> {t("paid")}
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        {/* MAIN BODY */}
        <div className="invoice-main-content">
          <div className="invoice-amount-block">
            <span className="invoice-amount-value">
              {invoice.amount?.toLocaleString()} {invoice.currency}
            </span>
          </div>
          {/* Details as table for better organization */}
          <table className="invoice-details-table">
            <thead>
              <tr>
                <th>{t('label')}</th>
                <th>{t('value')}</th>
              </tr>
            </thead>
            <tbody>
              {details.map((detail, i) => (
                <tr key={i}>
                  <td>{detail.label}</td>
                  <td>{detail.value || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="invoice-sign-block">
            <span className="invoice-sign-label">{t("signature")}</span>
          </div>
        </div>
      </div>
    );
  }
);

export default PaymentInvoice;
